import { apiGetPrice } from '../../../../api/api';
import { getSchablonPricePerMonth } from '../../../../helper/cms';
import { PriceProduct } from '../../../../types/api_shared_types';
import { CmsProduct } from '../../../../types/cms_shared_types';
import { Product } from '../../../../types/webshop';
import { MusicClass, ProductConfigurationInputs } from './ProductConfigurationModal';

const MINIMUM_DELAY_FOR_SPINNER_IN_MS = 1000;

export const getProductFromConfiguration = async (
  cmsProduct: CmsProduct,
  values: ProductConfigurationInputs,
  selectedProduct?: Product,
): Promise<Product> => {
  const start = Date.now();

  if (values.customMeasureInputSelected) {
    values.measureInput = values.customMeasureInput;
  }
  if (values.customTimeMeasureInputSelected) {
    values.timeMeasureInput = values.customTimeMeasureInput;
  }

  const { priceYearInclVat, priceYearExclVat } = await getProductPrice(cmsProduct, selectedProduct, values);

  const quantity = cleanUpValue(values.measureInput);
  const timeQuantity = cleanUpValue(values.timeMeasureInput);
  const schablonQuantity = cmsProduct.isSchablon ? cleanUpValue(values.measureInput) : undefined;
  const schablonTimeQuantity = cmsProduct.isSchablon ? cleanUpValue(values.timeMeasureInput) : undefined;

  const end = Date.now();
  const timeTranspired = end - start;
  const waitingTime = !cmsProduct.isSchablon ? MINIMUM_DELAY_FOR_SPINNER_IN_MS - timeTranspired : 0;
  const musicClass = getMusicClass(cmsProduct, values);

  await delay(waitingTime);

  return {
    name: cmsProduct.name,
    code: cmsProduct.code,
    quantity: quantity,
    timeQuantity: timeQuantity,
    musicClass: musicClass,
    priceYearInclVat: Math.round((priceYearInclVat / 12) * 100) / 100,
    priceYearExclVat: Math.round((priceYearExclVat / 12) * 100) / 100,
    schablonQuantity: schablonQuantity,
    schablonTimeQuantity: schablonTimeQuantity,
    startDate: values.isSeasonallyOpen ? values.startDate : undefined,
    endDate: values.isSeasonallyOpen ? values.endDate : undefined,
    _timeQuantitySelection: values._timeMeasureInputSelection,
  };
};

export const getProductPrice = async (
  product: CmsProduct,
  selectedProduct: Product | undefined,
  values: ProductConfigurationInputs,
) => {
  if (product.isSchablon) {
    return {
      // variable names 'priceYear' not currently accurate as it is not always price per year
      priceYearInclVat: getSchablonPricePerMonth(product, selectedProduct?.priceYearInclVat ?? 0),
      priceYearExclVat: getSchablonPricePerMonth(product, selectedProduct?.priceYearExclVat ?? 0),
    };
  }

  const priceProduct: PriceProduct = {
    code: product.code,
    quantity: values.customMeasureInputSelected ? values.customMeasureInput : values.measureInput,
    timeQuantity: values.timeMeasureInput,
    musicClass: values.musicClassToggleInput ? MusicClass.B : MusicClass.A,
  };

  const { data } = await apiGetPrice(priceProduct);

  return { ...data };
};

const cleanUpValue = (value: string) => {
  return value.replace(/^0+/, '').replace('.', '').replace(' ', '');
};

const getMusicClass = (cmsProduct: CmsProduct, values: ProductConfigurationInputs) => {
  if (cmsProduct.isSchablon) {
    return cmsProduct.musicClass;
  }

  return values.musicClassToggleInput ? MusicClass.B : MusicClass.A;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
